import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { performAutoLogin, hasValidAuthData } from '../utils/autoLogin';

/**
 * Auto-login: skip during MS Teams OAuth; otherwise try token-based redirect on / and /login.
 */
export function AutoLoginWrapper() {
  const navigate = useNavigate();
  const location = useLocation();
  const MS_TEAMS_OAUTH_FLAG = 'msteams_oauth_in_progress';

  useEffect(() => {
    const search = location.search || '';
    const pathname = location.pathname;
    const hash = location.hash || '';
    const hasOAuthCode = search.includes('code=') && search.includes('state=');
    const isOAuthCallbackPath =
      pathname === '/ids/integrations/msteams/callback' ||
      pathname === '/ids/integrations/msteams/callback-new';
    const isOAuthCallbackHash = hash.includes('/ids/integrations/msteams/callback');
    const isFlagSet = sessionStorage.getItem(MS_TEAMS_OAUTH_FLAG);

    const isOAuthCallback = isOAuthCallbackPath || isOAuthCallbackHash || hasOAuthCode;

    if (isFlagSet || isOAuthCallback) {
      console.log('[msteams] Auto-login: Skipping - OAuth callback detected', {
        pathname,
        hasSearch: !!search,
        hasHash: !!hash,
      });
      return;
    }

    if (location.pathname === '/' || location.pathname === '/login') {
      if (hasValidAuthData()) {
        console.log('Auto-login: Valid authentication data found, redirecting...');
        performAutoLogin(navigate);
      } else {
        console.log('Auto-login: No valid authentication data found, staying on login page');
      }
    }
  }, [navigate, location.pathname, location.hash, location.search]);

  return null;
}

/**
 * Forces MS Teams OAuth callback onto the hash-router URL when needed.
 */
export function OAuthRedirectHandler() {
  const location = useLocation();

  const actualSearch = window.location.search || '';
  if (actualSearch.includes('code=') && actualSearch.includes('state=')) {
    try {
      sessionStorage.setItem('msteams_oauth_in_progress', '1');
    } catch (err) {
      // ignore storage errors
    }
  }

  useEffect(() => {
    const actualPathname = window.location.pathname;
    const actSearch = window.location.search || '';
    const locationPathname = location.pathname;
    const locationSearch = location.search || '';
    const hash = location.hash || '';

    console.log('[msteams] OAuthRedirectHandler checking', {
      actualPathname,
      reactPathname: locationPathname,
      actualSearch: actSearch.substring(0, 80),
      reactSearch: locationSearch.substring(0, 80),
      hash: hash.substring(0, 50),
    });

    const hasOAuthParams =
      (actSearch.includes('code=') && actSearch.includes('state=')) ||
      (locationSearch.includes('code=') && locationSearch.includes('state='));

    const isCallbackPath =
      actualPathname.includes('/ids/integrations/msteams/callback') ||
      locationPathname === '/ids/integrations/msteams/callback' ||
      locationPathname === '/ids/integrations/msteams/callback-new';

    console.log('[msteams] OAuthRedirectHandler detection', {
      hasOAuthParams,
      isCallbackPath,
      actualPathnameIncludes: actualPathname.includes('/ids/integrations/msteams/callback'),
    });

    if (hasOAuthParams && isCallbackPath) {
      console.log('[msteams] ✅ Detected OAuth callback, forcing hard redirect to hash route');
      sessionStorage.setItem('msteams_oauth_in_progress', '1');
      const searchToUse = actSearch || locationSearch;
      const baseUrl =
        window.location.origin +
        window.location.pathname.replace(/\/ids\/integrations\/msteams\/callback(-new)?/, '');
      const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
      const newUrl = `${cleanBaseUrl}#/ids/integrations/msteams/callback${searchToUse}`;
      console.log('[msteams] Hard redirecting to:', newUrl);
      window.location.href = newUrl;
    } else if (hash.includes('/ids/integrations/msteams/callback') && hash.includes('code=')) {
      console.log('[msteams] Callback already in hash format, component should handle it');
    }
  }, [location.pathname, location.search, location.hash]);

  return null;
}
